import { useEffect, useState } from 'react';

declare global {
  interface Window {
    // monday-sdk-js attaches itself to window in CDN mode; the npm package
    // exports a default init() function instead.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mondaySdk?: any;
  }
}

export interface MondayColumnValue {
  id: string;
  title: string;
  value: string | null;
  text: string | null;
}

export interface MondayItem {
  id: string;
  name: string;
  columnValues: MondayColumnValue[];
}

interface UseMondayBoardItemsResult {
  items: MondayItem[];
  boardId: string | null;
  loading: boolean;
  error: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let mondaySdkInstance: any = null;

async function getMondaySdk() {
  if (mondaySdkInstance) return mondaySdkInstance;
  const { default: mondayFactory } = await import('monday-sdk-js');
  mondaySdkInstance = mondayFactory();
  mondaySdkInstance.setApiVersion('2024-10');
  return mondaySdkInstance;
}

export function useMondayBoardItems(opts?: { enabled?: boolean }): UseMondayBoardItemsResult {
  const enabled = opts?.enabled !== false;
  const [items, setItems] = useState<MondayItem[]>([]);
  const [boardId, setBoardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setBoardId(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    // monday.get('context') uses postMessage and hangs indefinitely when there
    // is no parent Monday frame. Skip the SDK entirely when running standalone.
    if (window.self === window.top) {
      setLoading(false);
      return;
    }

    async function init() {
      try {
        const monday = await getMondaySdk();

        const ctx = await monday.get('context');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const id: string | undefined = ctx?.data?.boardId?.toString();

        if (!id) {
          if (!cancelled) {
            setLoading(false);
            setError('No board context — open this view inside a Monday board.');
          }
          return;
        }

        if (!cancelled) setBoardId(id);

        const result = await monday.api(`
          query ($boardIds: [ID!]!) {
            boards(ids: $boardIds) {
              items_page(limit: 200) {
                items {
                  id
                  name
                  column_values {
                    id
                    title: column { title }
                    value
                    text
                  }
                }
              }
            }
          }
        `, { variables: { boardIds: [id] } });

        if (cancelled) return;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const raw = result?.data?.boards?.[0]?.items_page?.items ?? [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mapped: MondayItem[] = (raw as any[]).map((item: any) => ({
          id: String(item.id),
          name: String(item.name),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          columnValues: (item.column_values ?? []).map((cv: any) => ({
            id: String(cv.id),
            title: String(cv.title?.title ?? cv.id),
            value: cv.value ?? null,
            text: cv.text ?? null,
          })),
        }));

        setItems(mapped);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setLoading(false);
        }
      }
    }

    void init();
    return () => { cancelled = true; };
  }, [enabled]);

  return { items, boardId, loading, error };
}
